import { VolumeOff, VolumeUp } from "@mui/icons-material";
import { Collapse } from "@mui/material";
import React, { useState } from "react";

import ButtonIconContainer from "../../../ButtonIconContainer/ButtonIconContainer";
import { Container, VolumeSlider, VolumeSliderContainer } from "./ButtonVolume.elements";

type ButtonVolumeProps = {
  volume: number;
  muted: boolean;
  onClickMuteVolume: () => void;
  onChangeVolumeSlider: (event: Event, value: number | number[]) => void;
  onCommitVolumeSlider: (event: Event, value: number | number[]) => void;
  forceShowVolumeSlider: boolean;
};

export default function ButtonVolume({
  volume,
  muted,
  onClickMuteVolume,
  onChangeVolumeSlider,
  onCommitVolumeSlider,
  forceShowVolumeSlider,
}: ButtonVolumeProps): React.ReactElement {
  const [isVolumeSliderShown, setIsVolumeSliderShown] = useState(false);
  const [isHovering, setIsHovering] = useState(false);

  const handleMouseEnterVolumeSlider = () => {
    setIsHovering(true);
  };

  const handleMouseLeaveVolumeSlider = () => {
    setIsHovering(false);
  };

  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;

  const tabIndex = 1;

  React.useEffect(() => {
    setIsVolumeSliderShown(forceShowVolumeSlider || isHovering);
  }, [forceShowVolumeSlider, isHovering]);

  return (
    <Container
      onFocus={handleMouseEnterVolumeSlider}
      onMouseEnter={handleMouseEnterVolumeSlider}
      onMouseLeave={handleMouseLeaveVolumeSlider}
    >
      <ButtonIconContainer
        ariaLabel={`volume, press enter to ${volume === 0 || muted ? "unmute" : "mute"}`}
        onClick={onClickMuteVolume}
        tooltip="Volume (up/down arrows)"
        id="button_volume"
        tabIndex={tabIndex}
      >
        {muted ? <VolumeOff /> : <VolumeUp />}
      </ButtonIconContainer>
      <Collapse timeout={{ appear: 300, enter: 300, exit: 2000 }} in={isVolumeSliderShown} orientation="horizontal">
        {!iOS && (
          <VolumeSliderContainer>
            <VolumeSlider
              aria-label={`Volume slider value: ${volume}`}
              aria-valuetext={`${Math.round(volume * 100)}`}
              value={volume}
              color="primary"
              onChange={onChangeVolumeSlider}
              onChangeCommitted={onCommitVolumeSlider}
              min={0}
              max={1}
              step={0.01}
              size="small"
              tabIndex={tabIndex}
              id="volume_slider"
            />
          </VolumeSliderContainer>
        )}
      </Collapse>
    </Container>
  );
}
